import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

export const DAY = 864e5;

const get = (url, fallback) => api.get(url).then((r) => r.data ?? fallback).catch(() => fallback);

// Each key is undefined while loading, null on error, otherwise data.
export function usePortalData() {
  const [d, setD] = useState({});
  const load = useCallback(() => {
    const put = (k, p) => p.then((v) => setD((s) => ({ ...s, [k]: v })));
    return Promise.all([
      put("apps", get("/me/applications", null)),
      put("interviews", get("/me/interviews", [])),
      put("opp", get("/opportunities", null)),
      put("profile", get("/profile/me", null)),
      put("auto", get("/autoapply", null)),
    ]);
  }, []);
  useEffect(() => { load(); }, [load]);
  return { ...d, reload: load, setData: setD };
}

export function useSavedJobs(uid) {
  const key = `hs_saved_jobs_${uid}`;
  const [ids, setIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
  });
  const toggle = (id) => setIds((cur) => {
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    localStorage.setItem(key, JSON.stringify(next));
    return next;
  });
  return [ids, toggle];
}

export const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";
export const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : "");
export const fmtTime = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
export const fmtDayTime = (iso) => (iso ? `${fmtDay(iso)}, ${fmtTime(new Date(iso))}` : "");

export function ago(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - Date.parse(iso)) / DAY);
  if (d <= 0) return "today";
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / DAY);
  if (d.toDateString() === new Date().toDateString()) return "Today";
  if (diff < 6) return d.toLocaleDateString(undefined, { weekday: "short" });
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export const fitTone = (f) => (f >= 70 ? "text-primary" : f >= 45 ? "text-amber-600 dark:text-amber-300" : "text-muted-foreground");
export const TONE = { green: "text-primary", amber: "text-amber-600 dark:text-amber-300", blue: "text-sky-600 dark:text-sky-300", muted: "text-muted-foreground" };
export const companyLine = (company, job) => [company, job?.location].filter(Boolean).join(" · ");

export const GROUPS = [
  ["needs", "Needs you", "text-amber-600 dark:text-amber-300"],
  ["waiting", "Waiting on employer", "text-sky-600 dark:text-sky-300"],
  ["closed", "Closed", "text-muted-foreground"],
];
const GROUP_ORDER = { needs: 0, waiting: 1, closed: 2 };
export const byGroup = (x, y) => GROUP_ORDER[x.s.group] - GROUP_ORDER[y.s.group];

export const upcomingInterview = (a, ivs) => (ivs || [])
  .filter((i) => i.candidate_id === a.id && i.status !== "cancelled" && Date.parse(i.when) > Date.now() - 36e5)
  .sort((x, y) => Date.parse(x.when) - Date.parse(y.when))[0];

// How many of Applied · Assessment · Interview · Offer are done.
export function stageSteps(a) {
  const tested = ["scoring", "completed"].includes(a.assessment?.status) || a.overall_score != null;
  if (["offer", "hired"].includes(a.stage) || a.offer) return 4;
  if (a.stage === "interview") return 3;
  return tested ? 2 : 1;
}

export function appStatus(a, ivs) {
  const st = a.assessment?.status;
  const iv = upcomingInterview(a, ivs);
  const applied = `Applied ${ago(a.applied_at)}`;
  if (a.stage === "offer" && a.offer && !a.offer.response)
    return { key: "offer", group: "needs", tone: "green", label: "Offer received", brief: "Offer · reply needed", when: a.offer.start_date ? `Start ${a.offer.start_date}` : "Waiting on your reply", action: "Review offer", primary: true };
  if (a.stage === "hired")
    return { key: "hired", group: "closed", tone: "green", label: "Hired · offer accepted", brief: "Hired", when: fmtDay(a.stage_entered_at), action: "View" };
  if (a.stage === "rejected") {
    const declined = a.offer?.response === "declined";
    return { key: declined ? "declined" : "rejected", group: "closed", tone: "muted", label: declined ? "You declined the offer" : "Not selected", brief: declined ? "Offer declined" : "Not selected", when: fmtDay(a.stage_entered_at), action: !declined && a.competencies?.length ? "Read feedback" : "View" };
  }
  if (["not_started", "in_progress"].includes(st) && ["applied", "screening"].includes(a.stage)) {
    const label = st === "in_progress" ? "Assessment in progress" : "Assessment to complete";
    return { key: "assessment", group: "needs", tone: "amber", label, brief: label, when: applied, action: st === "in_progress" ? "Resume" : "Start", primary: true, to: `/candidate/apply/${a.job_id}` };
  }
  if (iv) return { key: "interview", group: "waiting", tone: "blue", label: "Interview scheduled", brief: `Interview ${fmtDay(iv.when)}`, when: fmtDayTime(iv.when), action: "Prep", iv };
  if (a.stage === "interview") return { key: "interview-stage", group: "waiting", tone: "blue", label: "Interview stage", brief: "Interview stage", when: "Time to be confirmed", action: "View" };
  if (st === "scoring") return { key: "review", group: "waiting", tone: "muted", label: "Assessment being scored", brief: "Scoring · human reviewed", when: applied, action: "View" };
  return { key: "review", group: "waiting", tone: "muted", label: "Under review", brief: `With recruiter · ${ago(a.stage_entered_at || a.applied_at)}`, when: applied, action: "View" };
}

export function filterJobs(opps, { q, loc, remote, savedOnly, saved, sort }) {
  const term = q.trim().toLowerCase();
  const locs = loc.toLowerCase().split(",").map((s) => s.trim()).filter(Boolean);
  const out = opps.filter(({ job: j, company_name }) => {
    const hay = [j.title, company_name, j.department, j.location, ...(j.keywords || [])].join(" ").toLowerCase();
    const where = (j.location || "").toLowerCase();
    return (!term || hay.includes(term)) && (!locs.length || locs.some((l) => where.includes(l)))
      && (!remote || where.includes("remote")) && (!savedOnly || saved.includes(j.id));
  });
  const ts = (o) => Date.parse(o.job.published_at || o.job.created_at || 0) || 0;
  return out.sort((a, b) => (sort === "new" ? ts(b) - ts(a) : (b.fit || 0) - (a.fit || 0)));
}

export const prepBullets = (t) => String(t || "").split("\n").map((l) => l.trim()).filter((l) => /^[-*•]\s+/.test(l)).map((l) => l.replace(/^[-*•]\s+/, ""));
